import ArticleIcon from "@mui/icons-material/Article";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import ExploreIcon from "@mui/icons-material/Explore";
import InsightsIcon from "@mui/icons-material/Insights";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import UpdateIcon from "@mui/icons-material/Update";
import {
  Box,
  Container,
  Stack,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedSection from "screens/Shared/AnimationSection";
import homeStyles from "../Home.styles";

// Benefits data - All using orange with varying gradient intensities
const benefits = [
  {
    id: 1,
    title: "Daily Fresh Content",
    description:
      "Get one new blog post every day covering each of our 7 diverse topics",
    icon: ArticleIcon,
    gradientIntensity: 1, // Strongest orange
  },
  {
    id: 2,
    title: "Diverse Topics",
    description:
      "Explore 7 comprehensive categories from Hydroponics to Social Entrepreneurship",
    icon: MenuBookIcon,
    gradientIntensity: 0.7,
  },
  {
    id: 3,
    title: "Expert Insights",
    description:
      "Learn from industry experts and thought leaders across multiple domains",
    icon: InsightsIcon,
    gradientIntensity: 0.9,
  },
  {
    id: 4,
    title: "Regular Updates",
    description:
      "Stay current with consistently updated content and trending topics",
    icon: UpdateIcon,
    gradientIntensity: 0.6,
  },
  {
    id: 5,
    title: "Easy Navigation",
    description:
      "Discover and explore content effortlessly with our intuitive interface",
    icon: ExploreIcon,
    gradientIntensity: 0.8,
  },
  {
    id: 6,
    title: "Quality Content",
    description:
      "Access well-researched, high-quality articles written by professionals",
    icon: AutoAwesomeIcon,
    gradientIntensity: 0.75,
  },
  {
    id: 7,
    title: "Stay Informed",
    description:
      "Keep up with the latest trends and developments in your areas of interest",
    icon: TrendingUpIcon,
    gradientIntensity: 0.85,
  },
  {
    id: 8,
    title: "Community Engagement",
    description:
      "Join a community of readers passionate about learning and growth",
    icon: Diversity3Icon,
    gradientIntensity: 0.65,
  },
];

// Timeline Benefit Item Component
const TimelineBenefitItem = ({
  benefit,
  index,
  muiTheme,
  classes,
  isHovered,
  onHover,
  mdUp,
}: any) => {
  const IconComponent = benefit.icon;
  const isEven = index % 2 === 0;
  const intensity = benefit.gradientIntensity;

  return (
    <motion.div
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ scale: 1.02 }}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: mdUp
          ? isEven
            ? "flex-start"
            : "flex-end"
          : "flex-start",
        width: "100%",
        marginBottom: mdUp ? "32px" : "24px",
      }}
    >
      {/* Content Box */}
      <Box
        sx={{
          ...classes.timelineBenefitContent,
          background:
            muiTheme.palette.mode === "dark"
              ? alpha(muiTheme.palette.background.paper, 0.5)
              : alpha(muiTheme.palette.background.paper, 0.7),
          border: `2px solid ${alpha(
            "#f8481c",
            muiTheme.palette.mode === "dark" ? 0.4 : 0.3
          )}`,
          maxWidth: mdUp ? "48%" : "90%",
          marginLeft: mdUp ? (isEven ? 0 : "auto") : 0,
          marginRight: mdUp ? (isEven ? "auto" : 0) : 0,
          transform: "translateY(-2px)",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Icon Badge */}
        <Box
          sx={{
            ...classes.timelineBenefitIconBadge,
            background: `linear-gradient(135deg, ${alpha(
              "#f8481c",
              0.1
            )} 0%, ${alpha("#f8481c", 0.08)} 100%)`,
            border: `2px solid ${alpha("#f8481c", 0.3)}`,
            boxShadow: `0 0 12px ${alpha("#f8481c", 0.15)}`,
            transition: "all 0.3s ease",
          }}
        >
          <motion.div
            animate={{
              scale: isHovered ? 1.15 : 1,
              rotate: isHovered ? [0, -10, 10, -5, 0] : 0,
            }}
            transition={{
              scale: { duration: 0.3 },
              rotate: { duration: 0.6, ease: "easeInOut" },
            }}
          >
            <IconComponent
              sx={{
                ...classes.timelineBenefitIcon,
                color: "#f8481c",
                transition: "all 0.3s ease",
              }}
            />
          </motion.div>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, ml: 2, position: "relative" }}>
          {/* Orange accent line */}
          <Box
            sx={
              {
                // position: "absolute",
                // left: "-12px",
                // top: 0,
                // bottom: 0,
                // width: "3px",
                // background: `linear-gradient(to bottom, ${alpha("#f8481c", 0.5)} 0%, ${alpha("#f8481c", 0.3)} 50%, ${alpha("#f8481c", 0.5)} 100%)`,
                // borderRadius: "2px",
                // transition: "opacity 0.3s ease",
              }
            }
          />
          <Typography
            sx={{
              ...classes.timelineBenefitTitle,
              color: muiTheme.palette.text.primary,
              transition: "all 0.3s ease",
            }}
          >
            {benefit.title}
          </Typography>
          <Typography
            sx={{
              ...classes.timelineBenefitDescription,
              color: muiTheme.palette.text.secondary,
              transition: "all 0.3s ease",
              "&::first-letter": {
                fontSize: "1.3em",
                fontWeight: 700,
                color: "#f8481c",
                lineHeight: 1,
                marginRight: "2px",
                transition: "color 0.3s ease",
              },
            }}
          >
            {benefit.description}
          </Typography>
        </Box>

        {/* Number Badge */}
        <Box
          sx={{
            ...classes.timelineBenefitNumber,
            background: `linear-gradient(135deg, ${alpha(
              "#f8481c",
              0.2
            )} 0%, ${alpha("#f8481c", 0.15)} 100%)`,
            border: `2px solid ${alpha("#f8481c", 0.5)}`,
            color: "#f8481c",
            fontWeight: 700,
            boxShadow: `0 0 12px ${alpha("#f8481c", 0.3)}`,
            transition: "all 0.3s ease",
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </Box>
      </Box>

      {/* Connecting Line to Center (Desktop only) - behind cards */}
      {mdUp && (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: isEven ? "calc(50% - 180px)" : "calc(50% - 180px)",
            height: "2px",
            background: `linear-gradient(${
              isEven ? "to right" : "to left"
            }, ${alpha("#f8481c", 0.3)} 0%, ${alpha(
              "#f8481c",
              0.2
            )} 50%, transparent 100%)`,
            boxShadow: `0 0 6px ${alpha("#f8481c", 0.3)}`,
            transition: "all 0.3s ease",
            zIndex: -1,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Dot at intersection with center line (Desktop only) */}
      {mdUp && (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background:
              muiTheme.palette.mode === "dark"
                ? `radial-gradient(circle, ${alpha(
                    muiTheme.palette.divider,
                    0.6
                  )} 0%, ${alpha(muiTheme.palette.divider, 0.3)} 100%)`
                : `radial-gradient(circle, ${alpha(
                    "#222222",
                    0.25
                  )} 0%, ${alpha("#222222", 0.12)} 100%)`,
            border:
              muiTheme.palette.mode === "dark"
                ? `2px solid ${alpha(muiTheme.palette.divider, 0.5)}`
                : `2px solid ${alpha("#222222", 0.2)}`,
            boxShadow:
              muiTheme.palette.mode === "dark"
                ? `0 0 8px ${alpha(muiTheme.palette.divider, 0.3)}`
                : `0 0 8px ${alpha("#222222", 0.15)}`,
            zIndex: -1,
            pointerEvents: "none",
          }}
        />
      )}
    </motion.div>
  );
};

const BenefitsSection = () => {
  const muiTheme = useTheme();
  const classes = homeStyles;
  const { t } = useTranslation();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <Box
      ref={containerRef}
      py={{ md: 10, xs: 6 }}
      sx={{
        position: "relative",
        backgroundColor: muiTheme.palette.background.default,
        overflow: "hidden",
      }}
    >
      {/* Static Creative Background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {/* Static Gradient Mesh */}
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            left: "-20%",
            right: "-20%",
            bottom: "-20%",
            width: "140%",
            height: "140%",
            background:
              muiTheme.palette.mode === "dark"
                ? "radial-gradient(ellipse at 15% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(ellipse at 85% 75%, rgba(248, 72, 28, 0.08) 0%, transparent 50%), radial-gradient(ellipse at 50% 50%, rgba(200, 200, 200, 0.06) 0%, transparent 50%)"
                : "radial-gradient(ellipse at 15% 25%, rgba(0, 0, 0, 0.06) 0%, transparent 50%), radial-gradient(ellipse at 85% 75%, rgba(248, 72, 28, 0.06) 0%, transparent 50%), radial-gradient(ellipse at 50% 50%, rgba(100, 100, 100, 0.04) 0%, transparent 50%)",
            filter: "blur(60px)",
            opacity: 0.7,
          }}
        />

        {/* Static Grid Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              muiTheme.palette.mode === "dark"
                ? `linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px)`
                : `linear-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(0, 0, 0, 0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 70%)",
            opacity: 0.4,
          }}
        />

        {/* Static Orb Clusters */}
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={`orb-cluster-${index}`}
            sx={{
              position: "absolute",
              left: `${10 + index * 22}%`,
              top: `${15 + index * 20}%`,
            }}
          >
            {[0, 1].map((subIndex) => (
              <Box
                key={`orb-${index}-${subIndex}`}
                sx={{
                  position: "absolute",
                  left: `${subIndex * 70}px`,
                  top: `${subIndex * 60}px`,
                  width: `${100 + subIndex * 50}px`,
                  height: `${100 + subIndex * 50}px`,
                  borderRadius: "50%",
                  background:
                    index === 1 && subIndex === 0
                      ? muiTheme.palette.mode === "dark"
                        ? `radial-gradient(circle, rgba(248, 72, 28, 0.15) 0%, rgba(248, 72, 28, 0.08) 50%, transparent 80%)`
                        : `radial-gradient(circle, rgba(248, 72, 28, 0.1) 0%, rgba(248, 72, 28, 0.05) 50%, transparent 80%)`
                      : muiTheme.palette.mode === "dark"
                      ? `radial-gradient(circle, rgba(255, 255, 255, ${
                          0.12 - subIndex * 0.02
                        }) 0%, rgba(240, 240, 240, ${
                          0.08 - subIndex * 0.015
                        }) 50%, transparent 80%)`
                      : `radial-gradient(circle, rgba(0, 0, 0, ${
                          0.08 - subIndex * 0.015
                        }) 0%, rgba(50, 50, 50, ${
                          0.06 - subIndex * 0.01
                        }) 50%, transparent 80%)`,
                  filter: "blur(25px)",
                  opacity: 0.5,
                }}
              />
            ))}
          </Box>
        ))}

        {/* Static Blob Shapes */}
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={`blob-${index}`}
            sx={{
              position: "absolute",
              left: `${8 + index * 22}%`,
              top: `${12 + index * 20}%`,
              width: `${220 + index * 70}px`,
              height: `${220 + index * 70}px`,
              borderRadius:
                index % 2 === 0
                  ? "30% 70% 70% 30% / 30% 30% 70% 70%"
                  : "70% 30% 30% 70% / 70% 70% 30% 30%",
              background:
                muiTheme.palette.mode === "dark"
                  ? `linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(220, 220, 220, 0.05) 50%, rgba(200, 200, 200, 0.03) 100%)`
                  : `linear-gradient(135deg, rgba(0, 0, 0, 0.06) 0%, rgba(50, 50, 50, 0.04) 50%, rgba(100, 100, 100, 0.02) 100%)`,
              filter: "blur(35px)",
              border: `1px solid ${alpha(
                muiTheme.palette.divider,
                muiTheme.palette.mode === "dark" ? 0.08 : 0.05
              )}`,
              opacity: 0.4,
            }}
          />
        ))}

        {/* Static Geometric Shapes */}
        {[0, 1, 2, 3, 4].map((index) => {
          const shapes = [
            "polygon(50% 0%, 0% 100%, 100% 100%)",
            "polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)",
            "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
          ];
          return (
            <Box
              key={`geo-${index}`}
              sx={{
                position: "absolute",
                left: `${12 + (index % 3) * 30}%`,
                top: `${18 + Math.floor(index / 3) * 25}%`,
                width: `${70 + (index % 2) * 40}px`,
                height: `${70 + (index % 2) * 40}px`,
                clipPath: shapes[index % shapes.length],
                background:
                  index === 2
                    ? muiTheme.palette.mode === "dark"
                      ? `linear-gradient(135deg, rgba(248, 72, 28, 0.12) 0%, rgba(248, 72, 28, 0.08) 50%, rgba(248, 72, 28, 0.05) 100%)`
                      : `linear-gradient(135deg, rgba(248, 72, 28, 0.1) 0%, rgba(248, 72, 28, 0.06) 50%, rgba(248, 72, 28, 0.04) 100%)`
                    : muiTheme.palette.mode === "dark"
                    ? `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(220, 220, 220, 0.06) 50%, rgba(200, 200, 200, 0.04) 100%)`
                    : `linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0%, rgba(50, 50, 50, 0.05) 50%, rgba(100, 100, 100, 0.03) 100%)`,
                border: `1px solid ${alpha(
                  index === 2 ? "#f8481c" : muiTheme.palette.divider,
                  index === 2
                    ? muiTheme.palette.mode === "dark"
                      ? 0.2
                      : 0.15
                    : muiTheme.palette.mode === "dark"
                    ? 0.1
                    : 0.07
                )}`,
                filter: "blur(1px)",
                opacity: 0.3,
              }}
            />
          );
        })}
      </Box>

      {/* Dark Overlay for Dark Theme */}
      {muiTheme.palette.mode === "dark" && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            pointerEvents: "none",
            zIndex: 0.5,
          }}
        />
      )}

      <style>
        {`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 145% 50%;
            }
          }
        `}
      </style>
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Stack
          direction="column"
          spacing={3}
          textAlign="center"
          alignItems="center"
          mb={{ md: 6, xs: 4 }}
        >
          <AnimatedSection animationType="fadeInUp">
            <Typography
              sx={{
                ...classes.contentHeaderText,
                color: muiTheme.palette.text.primary,
                maxWidth: "900px",
                fontSize: { xs: "28px", md: "42px" },
                lineHeight: 1.2,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                background:
                  muiTheme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 65%, #f8481c 85%, #ff6b4a 100%)"
                    : "linear-gradient(135deg, #000000 0%, #000000 65%, #f8481c 85%, #ff6b4a 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundSize: "300% 300%",
                animation: "gradientShift 5s ease infinite",
              }}
            >
              Why Choose Our Blog Platform?
            </Typography>
          </AnimatedSection>
          <AnimatedSection animationType="fadeInUp">
            <Typography
              sx={{
                ...classes.subHeaderText,
                color: muiTheme.palette.text.secondary,
                maxWidth: "700px",
                fontSize: { xs: "15px", md: "17px" },
                lineHeight: 1.6,
                "&::first-letter": {
                  fontSize: "1.5em",
                  fontWeight: 700,
                  color: alpha("#f8481c", 0.6),
                  lineHeight: 1,
                  marginRight: "4px",
                },
              }}
            >
              Discover the benefits of staying connected with our comprehensive
              blog platform featuring daily content across diverse topics
            </Typography>
            {/* Subtle accent line under description */}
            <Box
              sx={{
                width: "80px",
                height: "2px",
                background: `linear-gradient(90deg, ${alpha(
                  muiTheme.palette.divider,
                  0.3
                )} 0%, ${alpha("#f8481c", 0.4)} 50%, ${alpha(
                  muiTheme.palette.divider,
                  0.3
                )} 100%)`,
                borderRadius: "1px",
                mt: 2,
                mx: "auto",
              }}
            />
          </AnimatedSection>
        </Stack>

        {/* Timeline Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
            py: { md: 3, xs: 2 },
          }}
        >
          {/* Central Timeline Line with Dots (Desktop only) */}
          {mdUp && (
            <>
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  transform: "translateX(-50%)",
                  background: `linear-gradient(to bottom, transparent 0%, ${alpha(
                    "#f8481c",
                    0.2
                  )} 10%, ${alpha("#f8481c", 0.35)} 30%, ${alpha(
                    "#f8481c",
                    0.3
                  )} 50%, ${alpha("#f8481c", 0.35)} 70%, ${alpha(
                    "#f8481c",
                    0.2
                  )} 90%, transparent 100%)`,
                  zIndex: -1,
                  boxShadow: `0 0 8px ${alpha("#f8481c", 0.2)}`,
                }}
              />
            </>
          )}

          {/* Benefits Timeline Items */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {benefits.map((benefit, index) => (
              <TimelineBenefitItem
                key={benefit.id}
                benefit={benefit}
                index={index}
                muiTheme={muiTheme}
                classes={classes}
                isHovered={hoveredIndex === index}
                onHover={setHoveredIndex}
                mdUp={mdUp}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BenefitsSection;
